import { Magnolia } from "domain/Magnolia";
import { getMultisiteDefaultLanguage } from "util/multisiteLanguages";

/**
 * Is running on Magnolia server, either in editor or on public instance.
 */
export function onMagnolia() {
  return process.env.REACT_APP_MGNL_IS_PREVIEW;
}

export function getAPIBase() {
  let M;
  if (Boolean(process.env.REACT_APP_MGNL_IS_PREVIEW)) {
    M = process.env.REACT_APP_MGNL_BASE_AUTHOR;
  } else {
    M = process.env.REACT_APP_MGNL_BASE_PUBLIC;
  }
  let API_BASE = process.env.REACT_APP_MGNL_HOST! + M;
  return API_BASE;
}

export function getLanguages() {
  return process.env.REACT_APP_MGNL_LANGUAGES!.split(" ");
}

export function removeCurrentLanguage(string: string, currentLanguage: string) {
  return string.replace(new RegExp("/" + currentLanguage + "($|/)"), "/");
}

export function getCurrentLanguage() {
  const languages = getLanguages();

  // In Magnolia preview mode, check for explicit lang parameter first
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("mgnlPreview")) {
    // Check for explicit lang parameter in query string
    const langParam = urlParams.get("lang");
    if (langParam && languages.includes(langParam)) {
      return langParam;
    }
    
    // Check if language is in the URL path (e.g., /albis-distribution/de/page)
    for (let i = 0; i < languages.length; i++) {
      const language = languages[i];
      if (new RegExp("/" + language + "($|/)").test(window.location.pathname)) {
        return language;
      }
    }
    
    // Only use default if no language in query OR path
    const multisiteBase = getCurrentMultisiteBase();
    const defaultLang = getMultisiteDefaultLanguage(multisiteBase);
    if (languages.includes(defaultLang)) {
      return defaultLang;
    }
  }

  // Regular mode: detect language from URL path
  for (let i = 0; i < languages.length; i++) {
    const language = languages[i];

    if (new RegExp("/" + language + "($|/)").test(window.location.pathname)) {
      return language;
    }
  }

  // Fallback: return first language in array
  return languages[0];
}

export const getBrandFromLocation = (): Magnolia.MultisiteId => {
  if (window.location.pathname.startsWith("/albis-distribution")) {
    return "albis";
  } else if (window.location.pathname.startsWith("/mocom")) {
    return "mocom";
  } else if (window.location.pathname.startsWith("/krahn-ceramics")) {
    return "krahnCeramics";
  } else if (window.location.pathname.startsWith("/otto-krahn-group")) {
    return "ottoKrahnGroup";
  } else if (window.location.pathname.startsWith("/htf")) {
    return "htf";
  } else if (window.location.pathname.startsWith("/wipag")) {
    return "wipag";
  } else if (window.location.pathname.startsWith("/plgrd")) {
    return "plgrd";
  } else if (window.location.pathname.startsWith("/krahn-chemie")) {
    return "krahnChemie";
  } else if (window.location.pathname.startsWith("/otto-krahn-new-business")) {
    return "okNewBusiness";
  } else if (window.location.origin.includes("mocom.eu")) {
    return "mocom";
  } else if (window.location.origin.includes("albis.com")) {
    return "albis";
  } else if (window.location.origin.includes("krahn-ceramics.com")) {
    return "krahnCeramics";
  } else if (window.location.origin.includes("ottokrahn.group")) {
    return "ottoKrahnGroup";
  } else if (window.location.origin.includes("htf.krahn.eu")) {
    return "htf";
  } else if (window.location.origin.includes("wipag.de")) {
    return "wipag";
  } else if (window.location.origin.includes("wipag.com")) {
    return "wipag";
  } else if (window.location.origin.includes("krahn.eu")) {
    return "krahnChemie";
  } else if (window.location.origin.includes("oknb.eu")) {
    return "okNewBusiness";
  }
  return "albis";
};

export const getCurrentMultisiteBase = () => {
  // In preview mode, try to extract multisite from pathname
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("mgnlPreview")) {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    
    if (pathParts.length > 0) {
      const firstSegment = pathParts[0];
      
      // Known multisites
      const knownMultisites = [
        "albis-distribution",
        "mocom",
        "krahn-ceramics",
        "otto-krahn-group",
        "htf",
        "wipag",
        "krahn-chemie",
        "otto-krahn-new-business",
        "plgrd",
      ];
      
      // Check if it's a known multisite directly
      if (knownMultisites.includes(firstSegment)) {
        return firstSegment;
      }
      
      // Check if it has -old suffix and maps to a known multisite
      if (firstSegment.endsWith("-old")) {
        const baseMultisite = firstSegment.slice(0, -4); // Remove "-old"
        if (knownMultisites.includes(baseMultisite)) {
          return firstSegment; // Return with -old suffix intact
        }
      }
      
      // Not a known multisite or -old variant, fall through to brand-based detection
    }
  }
  
  // Non-preview mode OR no multisite detected in path: use existing brand mapping
  const brand = getBrandFromLocation();
  switch (brand) {
    case "albis":
      return "albis-distribution";
    case "mocom":
      return "mocom";
    case "krahnCeramics":
      return "krahn-ceramics";
    case "ottoKrahnGroup":
      return "otto-krahn-group";
    case "htf":
      return "htf";
    case "plgrd":
      return "plgrd";
    case "wipag":
      return "wipag";
    case "krahnChemie":
      return "krahn-chemie";
    case "okNewBusiness":
      return "otto-krahn-new-business";
    default:
      return "albis-distribution";
  }
};

/**
 * Check if we're running in admin instance by checking if domain contains "admin"
 * e.g. admin.albis.com or stage-admin.albis.com
 */
export function isAdminContext(): boolean {
  return window.location.hostname.includes("admin");
}
